import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { CheckCircle2 } from "lucide-react";
import { ABOUT_IMAGE } from "../lib/constants";
import { MaskedLines, Reveal } from "./motion";

const PILLARS = [
  "Prevención de accidentes laborales",
  "Optimización de pólizas y cargas de riesgo",
  "Bienestar físico, mental y social del colaborador",
  "Cumplimiento normativo y certificaciones",
];

export default function About() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], [50, -50]);

  return (
    <section
      id="nosotros"
      ref={ref}
      data-testid="about-section"
      className="relative overflow-hidden bg-background py-24 lg:py-32"
    >
      {/* Giant chapter number */}
      <span
        aria-hidden
        className="text-outline-green pointer-events-none absolute -right-8 top-8 hidden select-none font-display text-[17rem] font-extrabold leading-none lg:block"
      >
        01
      </span>

      <div className="mx-auto grid max-w-7xl gap-12 px-5 lg:grid-cols-12 lg:gap-20 lg:px-8">
        <div className="self-start lg:sticky lg:top-32 lg:col-span-5">
          <Reveal className="relative">
            <div className="overflow-hidden rounded-sm">
              <motion.img
                src={ABOUT_IMAGE}
                alt="Equipo Pino Verde"
                style={{ y: imgY, scale: 1.18 }}
                className="aspect-[4/5] w-full object-cover will-change-transform"
              />
            </div>
            <motion.div
              initial={{ opacity: 0, y: 24, rotate: -2 }}
              whileInView={{ opacity: 1, y: 0, rotate: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.35, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
              className="absolute -bottom-6 -right-6 hidden max-w-[220px] rounded-sm bg-pv-orange p-6 text-white sm:block"
            >
              <div className="font-display text-5xl font-extrabold leading-none">
                23
              </div>
              <div className="mt-1 text-sm font-medium">
                años cuidando a las personas que mueven Costa Rica.
              </div>
            </motion.div>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <Reveal>
            <div className="overline mb-4 text-pv-orange">
              Capítulo 01 — Manifiesto
            </div>
          </Reveal>
          <MaskedLines
            className="font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-pv-green sm:text-5xl lg:text-6xl"
            lines={[
              <>Creemos que las empresas</>,
              <span className="text-pv-lime">cuidan a las personas</span>,
              <>previniendo accidentes.</>,
            ]}
          />

          <Reveal delay={0.2}>
            <div className="mt-8 space-y-5 text-base leading-relaxed text-slate-700 lg:text-lg">
              <p>
                En <strong>Pino Verde Salud Ocupacional</strong> somos
                especialistas con más de dos décadas estableciendo y sosteniendo
                ambientes de trabajo seguros y sanos para la industria
                costarricense.
              </p>
              <p>
                Acompañamos a empresas pequeñas, medianas y grandes con un
                proceso claro: analizamos los riesgos, asesoramos al equipo y
                entregamos el equipo de protección personal exacto que cada
                puesto requiere.
              </p>
            </div>
          </Reveal>

          <div className="mt-10 grid gap-4 sm:grid-cols-2">
            {PILLARS.map((p, i) => (
              <Reveal key={p} delay={0.1 + i * 0.08}>
                <div
                  data-testid={`pillar-${i}`}
                  className="flex h-full items-start gap-3 rounded-sm border border-slate-200 bg-white p-4 transition-colors duration-300 hover:border-pv-lime"
                >
                  <CheckCircle2
                    size={20}
                    className="mt-0.5 shrink-0 text-pv-lime"
                  />
                  <span className="text-sm font-medium text-slate-800">{p}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
